import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Image } from "react-bootstrap";
import Container from "../components/Container";
import SectionHeader from "../components/SectionHeader";
import Card from "../components/Card";
import Button from "../components/Button";
import { Package, Leaf, TrendingUp, Award, Recycle, ArrowRight, Target, Users, GitBranch, ChevronDown } from "lucide-react";
import { motion, useInView, useAnimation } from "framer-motion";
import "../styles/components.css";

// Image Partenariat durable (locale)
const IMG_PARTENARIAT = "/images/partenariartdurable.jpg";
// Image de repli pour la section équipe si le fichier local est absent
const FALLBACK_EQUIPE = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop&q=80";
// Image fond section Économie circulaire et Partenariat durable (fixe au scroll)
const IMG_ECONOMIE = "/images/backgroundhome.jpg";

const Home = () => {
  const [, setCurrentSlide] = useState(0);
  const [imgEquipe, setImgEquipe] = useState("/images/equipe_professionnels.jpg");

  const slides = [
    {
      title: "Solution sur-mesure",
      description: "Parce que vous êtes unique, nous vous proposons une solution adaptée à votre besoin pour l'optimisation de la gestion et du tri de vos biens et déchets.",
      icon: Package,
      color: "var(--color-primary)"
    },
    {
      title: "Notre démarche",
      description: "Repose sur une approche scientifique qui prend en compte votre produit sur son cycle de vie. Nos recommandations reposent sur des process d'éco-conception et d'ACV.",
      icon: Leaf,
      color: "var(--color-secondary)"
    },
    {
      title: "Visibilité",
      description: "Nous communiquons dans nos médias sur les actions durables que vous mettez en place dans votre entreprise.",
      icon: TrendingUp,
      color: "var(--color-accent)"
    }
  ];

  // Nos Domaines d'Expertise (inspiré des maquettes)
  const domaines = [
    {
      title: "Gestion des déchets",
      description: "Solutions optimisées pour le tri, la collecte et le traitement de vos déchets.",
      icon: Recycle,
      iconBg: "var(--color-accent)",
      image: "https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=600&h=400&fit=crop&q=80"
    },
    {
      title: "Valorisation des biens",
      description: "Maximisation de la valeur de vos biens en fin de vie par le recyclage et la réutilisation.",
      icon: Target,
      iconBg: "var(--color-secondary)",
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop&q=80"
    },
    {
      title: "Éco-conception",
      description: "Conseil en process d'éco-conception et analyse du cycle de vie (ACV).",
      icon: GitBranch,
      iconBg: "var(--color-accent)",
      image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=600&h=400&fit=crop&q=80"
    },
    {
      title: "Solutions sur-mesure",
      description: "Accompagnement personnalisé pour particuliers, entreprises et collectivités.",
      icon: Users,
      iconBg: "#7C3AED",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop&q=80"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  // Refs pour animations
  const servicesRef = useRef(null);
  const domainesRef = useRef(null);
  const approcheRef = useRef(null);
  const economieRef = useRef(null);
  const partenariatRef = useRef(null);
  const aboutRef = useRef(null);

  const servicesInView = useInView(servicesRef, { once: true, margin: "-100px" });
  const domainesInView = useInView(domainesRef, { once: true, margin: "-100px" });
  const approcheInView = useInView(approcheRef, { once: true, margin: "-100px" });
  const economieInView = useInView(economieRef, { once: true, margin: "-100px" });
  const partenariatInView = useInView(partenariatRef, { once: true, margin: "-100px" });
  const aboutInView = useInView(aboutRef, { once: true, margin: "-100px" });

  const servicesControls = useAnimation();
  const domainesControls = useAnimation();
  const approcheControls = useAnimation();
  const economieControls = useAnimation();
  const partenariatControls = useAnimation();
  const aboutControls = useAnimation();

  useEffect(() => {
    if (servicesInView) servicesControls.start("visible");
  }, [servicesInView, servicesControls]);

  useEffect(() => {
    if (domainesInView) domainesControls.start("visible");
  }, [domainesInView, domainesControls]);

  useEffect(() => {
    if (approcheInView) approcheControls.start("visible");
  }, [approcheInView, approcheControls]);

  useEffect(() => {
    if (economieInView) economieControls.start("visible");
  }, [economieInView, economieControls]);

  useEffect(() => {
    if (partenariatInView) partenariatControls.start("visible");
  }, [partenariatInView, partenariatControls]);

  useEffect(() => {
    if (aboutInView) aboutControls.start("visible");
  }, [aboutInView, aboutControls]);

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const springTransition = { type: "spring", stiffness: 120, damping: 20 };
  const springSoft = { type: "spring", stiffness: 100, damping: 18 };

  return (
    <div style={{ overflow: 'hidden' }}>
      {/* Hero Section - dynamique et moderne */}
      <section className="hero">
        <div className="hero-shine" aria-hidden="true" />
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <Container>
            <Row className="align-items-center">
              <Col lg={8}>
                <motion.h1 
                  style={{ marginBottom: 'var(--spacing-lg)' }}
                  initial={{ opacity: 0, y: -30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.8, ...springSoft }}
                >
                  Votre partenaire pour la gestion, la valorisation et le recyclage
                </motion.h1>
                <motion.p 
                  style={{ fontSize: 'var(--font-size-xl)', marginBottom: 'var(--spacing-2xl)', opacity: 0.95 }}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 0.95, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.8, ...springSoft }}
                >
                  Nous accompagnons les particuliers, entreprises et collectivités dans leur transition écologique 
                  avec des solutions sur-mesure et une expertise reconnue.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.8, ...springSoft }}
                  style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}
                >
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} transition={springTransition}>
                    <Button variant="secondary" size="lg" onClick={() => window.location.href = '/apropos'}>
                      Découvrir notre savoir-faire
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} transition={springTransition}>
                    <Button 
                      variant="outline" size="lg" onClick={() => window.location.href = '/contact'}
                      style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 255, 255, 0.5)', color: 'white' }}
                    >
                      Nous contacter
                    </Button>
                  </motion.div>
                </motion.div>
              </Col>
            </Row>
          </Container>
        </motion.div>
        <motion.a
          href="#domaines-expertise"
          style={{
            position: 'absolute',
            bottom: 'var(--spacing-xl)',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255,255,255,0.9)',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: 'var(--font-size-xs)',
            textDecoration: 'none'
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          whileHover={{ y: 4 }}
        >
          <span>Découvrir</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ChevronDown size={28} strokeWidth={2.5} />
          </motion.span>
        </motion.a>
      </section>

      {/* Nos Domaines d'Expertise (inspiré maquette) */}
      <section id="domaines-expertise" className="section" style={{ backgroundColor: "#fff" }}>
        <Container>
          <motion.div
            ref={domainesRef}
            initial={{ opacity: 0, y: 30 }}
            animate={domainesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <SectionHeader
              title="Nos Domaines d'Expertise"
              subtitle="Nous mettons notre savoir-faire d'ingénieurs spécialisés en industrie et environnement au service de solutions innovantes et durables."
            />
          </motion.div>
          <motion.div
            initial="hidden"
            animate={domainesControls}
            variants={staggerContainer}
            className="row g-4"
            style={{ marginTop: "var(--spacing-3xl)" }}
          >
            {domaines.map((dom, index) => {
              const Icon = dom.icon;
              return (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="col-md-6 col-lg-3"
                  whileHover={{ y: -8, scale: 1.02, transition: springTransition }}
                  transition={springSoft}
                >
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "var(--border-radius-lg)",
                      overflow: "hidden",
                      boxShadow: "var(--shadow-sm)",
                      border: "1px solid rgba(2, 132, 199, 0.15)",
                      borderTop: "3px solid var(--color-accent)",
                      height: "100%",
                      transition: "box-shadow 0.3s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "var(--shadow-md)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                    }}
                  >
                    <div style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden" }}>
                      <Image
                        src={dom.image}
                        fluid
                        alt={dom.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.5s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "12px",
                          left: "12px",
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          background: dom.iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          boxShadow: "var(--shadow-sm)"
                        }}
                      >
                        <Icon size={24} />
                      </div>
                    </div>
                    <div style={{ padding: "var(--spacing-lg)" }}>
                      <h3
                        style={{
                          fontSize: "var(--font-size-xl)",
                          fontWeight: "var(--font-weight-semibold)",
                          marginBottom: "var(--spacing-sm)",
                          color: "var(--color-text)"
                        }}
                      >
                        {dom.title}
                      </h3>
                      <p
                        style={{
                          fontSize: "var(--font-size-base)",
                          color: "var(--color-text-light)",
                          lineHeight: "var(--line-height-relaxed)",
                          margin: 0
                        }}
                      >
                        {dom.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </Container>
      </section>

      {/* Notre approche intégrée */}
      <section className="section" style={{ backgroundColor: "var(--color-bg)" }}>
        <Container>
          <motion.div
            ref={approcheRef}
            initial={{ opacity: 0, y: 40 }}
            animate={approcheInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}
          >
            <h2
              style={{
                fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
                fontWeight: "var(--font-weight-bold)",
                marginBottom: "var(--spacing-lg)",
                color: "var(--color-text)"
              }}
            >
              Notre approche intégrée
            </h2>
            <p
              style={{
                fontSize: "var(--font-size-lg)",
                color: "var(--color-text-light)",
                lineHeight: "var(--line-height-relaxed)"
              }}
            >
              De l'analyse initiale à la mise en œuvre, nous vous accompagnons avec une vision globale pour optimiser vos processus et minimiser votre impact environnemental.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Économie circulaire (texte seul) - image en fond fixe au scroll (parallaxe) */}
      <section
        className="section"
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          backgroundImage: `linear-gradient(to bottom, rgba(250, 251, 252, 0.92) 0%, rgba(255, 255, 255, 0.9) 100%), url(${IMG_ECONOMIE})`,
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#fafbfc"
        }}
      >
        <Container style={{ position: "relative", zIndex: 1, width: "100%" }}>
          <motion.div
            ref={economieRef}
            initial="hidden"
            animate={economieControls}
            variants={staggerContainer}
          >
            <Row>
              <Col lg={12}>
                <motion.div variants={staggerItem}>
                  <h2
                    style={{
                      fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
                      fontWeight: "var(--font-weight-bold)",
                      marginBottom: "var(--spacing-lg)",
                      color: "var(--color-text)"
                    }}
                  >
                    Économie circulaire
                  </h2>
                  <p
                    style={{
                      fontSize: "var(--font-size-lg)",
                      color: "var(--color-text-light)",
                      lineHeight: "var(--line-height-relaxed)",
                      marginBottom: "var(--spacing-xl)"
                    }}
                  >
                    Nous privilégions une approche d'économie circulaire où chaque déchet devient une ressource. Notre expertise permet de transformer vos contraintes environnementales en opportunités économiques.
                  </p>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {[
                      { text: "Réduction des coûts de gestion", dot: "var(--color-secondary)" },
                      { text: "Création de nouvelles sources de revenus", dot: "var(--color-accent)" },
                      { text: "Amélioration de votre bilan environnemental", dot: "var(--color-primary)" }
                    ].map((item, i) => (
                      <li
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "var(--spacing-md)",
                          fontSize: "var(--font-size-base)",
                          color: "var(--color-text)"
                        }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: item.dot,
                            marginRight: "var(--spacing-md)",
                            flexShrink: 0
                          }}
                        />
                        {item.text}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Partenariat durable (image + texte + stats) - même image de fond fixe qu'Économie circulaire */}
      <section
        className="section"
        style={{
          position: "relative",
          backgroundImage: `linear-gradient(to bottom, rgba(250, 251, 252, 0.92) 0%, rgba(255, 255, 255, 0.9) 100%), url(${IMG_ECONOMIE})`,
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#fafbfc"
        }}
      >
        <Container style={{ position: "relative", zIndex: 1 }}>
          <motion.div
            ref={partenariatRef}
            initial="hidden"
            animate={partenariatControls}
            variants={staggerContainer}
          >
            <Row className="align-items-center g-5">
              <Col lg={5} md={6}>
                <motion.div
                  variants={staggerItem}
                  style={{
                    borderRadius: "var(--border-radius-xl)",
                    overflow: "hidden",
                    boxShadow: "var(--shadow-lg)"
                  }}
                  whileHover={{ scale: 1.02 }}
                >
                  <Image
                    src={IMG_PARTENARIAT}
                    fluid
                    alt="Partenariat durable - Terre et nature"
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                </motion.div>
              </Col>
              <Col lg={7} md={6}>
                <motion.div variants={staggerItem}>
                  <h2
                    style={{
                      fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
                      fontWeight: "var(--font-weight-bold)",
                      marginBottom: "var(--spacing-lg)",
                      color: "var(--color-text)"
                    }}
                  >
                    Partenariat durable
                  </h2>
                  <p
                    style={{
                      fontSize: "var(--font-size-lg)",
                      color: "var(--color-text-light)",
                      lineHeight: "var(--line-height-relaxed)",
                      marginBottom: "var(--spacing-xl)"
                    }}
                  >
                    Notre équipe d'ingénieurs diplômés de Polytech Nancy vous accompagne sur le long terme. Nous croyons en la construction de relations durables basées sur la confiance et l'excellence.
                  </p>
                  <Row className="g-3">
                    <Col xs={6} md={6}>
                      <motion.div
                        variants={{ hidden: { scale: 0.9, opacity: 0 }, visible: { scale: 1, opacity: 1, transition: springTransition } }}
                        style={{
                          padding: "var(--spacing-xl)",
                          borderRadius: "var(--border-radius-lg)",
                          background: "linear-gradient(135deg, rgba(2, 132, 199, 0.12) 0%, rgba(2, 132, 199, 0.06) 100%)",
                          border: "1px solid rgba(2, 132, 199, 0.2)"
                        }}
                        whileHover={{ y: -4, scale: 1.03 }}
                      >
                        <div style={{ fontSize: "2rem", fontWeight: "var(--font-weight-bold)", color: "var(--color-accent)", marginBottom: "0.25rem" }}>2025</div>
                        <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-light)" }}>Année de création</div>
                      </motion.div>
                    </Col>
                    <Col xs={6} md={6}>
                      <motion.div
                        variants={{ hidden: { scale: 0.9, opacity: 0 }, visible: { scale: 1, opacity: 1, transition: springTransition } }}
                        style={{
                          padding: "var(--spacing-xl)",
                          borderRadius: "var(--border-radius-lg)",
                          background: "linear-gradient(135deg, rgba(15, 118, 110, 0.12) 0%, rgba(15, 118, 110, 0.06) 100%)",
                          border: "1px solid rgba(15, 118, 110, 0.2)"
                        }}
                        whileHover={{ y: -4, scale: 1.03 }}
                      >
                        <div style={{ fontSize: "2rem", fontWeight: "var(--font-weight-bold)", color: "var(--color-primary)", marginBottom: "0.25rem" }}>100%</div>
                        <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-light)" }}>Solutions sur-mesure</div>
                      </motion.div>
                    </Col>
                  </Row>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Services Cards avec animations */}
      <section className="section">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <SectionHeader 
              title="Nos solutions"
              subtitle="Des services adaptés à vos besoins pour une gestion optimale de vos déchets"
            />
          </motion.div>
          
          <motion.div
            ref={servicesRef}
            initial="hidden"
            animate={servicesControls}
            variants={staggerContainer}
            className="grid-services"
            style={{ marginTop: 'var(--spacing-3xl)' }}
          >
            {slides.map((slide, index) => {
              const Icon = slide.icon;
              return (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  whileHover={{ y: -8, scale: 1.02, transition: springTransition }}
                  transition={springSoft}
                >
                  <Card
                    icon={Icon}
                    title={slide.title}
                    text={slide.description}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </Container>
      </section>

      {/* About Section - Une équipe de professionnels qualifiés (version d'origine) */}
      <section className="section" style={{
        background: 'linear-gradient(180deg, var(--color-bg) 0%, rgba(15, 118, 110, 0.02) 100%)'
      }}>
        <Container>
          <motion.div
            ref={aboutRef}
            initial="hidden"
            animate={aboutControls}
            variants={fadeInUp}
          >
            <Row className="align-items-center" style={{ marginBottom: 'var(--spacing-4xl)' }}>
              <Col lg={12}>
                <Row className="align-items-center g-5">
                  {/* Image à gauche */}
                  <Col lg={5} md={6} className="mb-4 mb-lg-0">
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      animate={aboutInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.2, duration: 0.8 }}
                      whileHover={{ scale: 1.02 }}
                      style={{ position: 'relative' }}
                    >
                      <div style={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 'var(--border-radius-xl)',
                        boxShadow: 'var(--shadow-xl)',
                        background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.1) 0%, rgba(2, 132, 199, 0.1) 100%)',
                        padding: 'var(--spacing-md)'
                      }}>
                        <Image
                          src={imgEquipe}
                          fluid
                          alt="Une équipe de professionnels qualifiés - Moov4Goods"
                          style={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: 'var(--border-radius-lg)',
                            display: 'block',
                            transition: 'transform 0.5s ease'
                          }}
                          onError={() => setImgEquipe(FALLBACK_EQUIPE)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.05) 0%, transparent 50%)',
                          pointerEvents: 'none',
                          borderRadius: 'var(--border-radius-lg)'
                        }} />
                      </div>
                    </motion.div>
                  </Col>

                  {/* Texte à droite */}
                  <Col lg={7} md={6}>
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      animate={aboutInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.4, duration: 0.8 }}
                    >
                      <motion.div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '0.5rem 1rem',
                          background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.1) 0%, rgba(2, 132, 199, 0.1) 100%)',
                          borderRadius: '50px',
                          marginBottom: 'var(--spacing-lg)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--color-primary)'
                        }}
                        initial={{ opacity: 0, y: -20 }}
                        animate={aboutInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.5 }}
                      >
                        <Award size={18} style={{ marginRight: '0.5rem' }} />
                        Expertise & Engagement
                      </motion.div>

                      <h2 style={{
                        fontSize: 'clamp(1.875rem, 4vw, 2.5rem)',
                        fontWeight: 'var(--font-weight-bold)',
                        marginBottom: 'var(--spacing-lg)',
                        color: 'var(--color-text)',
                        lineHeight: 'var(--line-height-tight)'
                      }}>
                        Une équipe de professionnels qualifiés
                      </h2>

                      <motion.p
                        style={{
                          fontSize: 'var(--font-size-lg)',
                          lineHeight: 'var(--line-height-relaxed)',
                          color: 'var(--color-text-light)',
                          marginBottom: 'var(--spacing-lg)'
                        }}
                        initial={{ opacity: 0 }}
                        animate={aboutInView ? { opacity: 1 } : {}}
                        transition={{ delay: 0.6 }}
                      >
                        Disponible pour vous livrer leur expertise et vous faciliter la prise en main de votre
                        conscience écologique. En étant acteur du développement durable, vous engagez des actions
                        permettant de limiter votre empreinte carbone et de réduire la consommation des ressources
                        de la planète.
                      </motion.p>

                      <motion.div
                        style={{
                          padding: 'var(--spacing-xl)',
                          background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.08) 0%, rgba(2, 132, 199, 0.08) 100%)',
                          borderRadius: 'var(--border-radius-lg)',
                          borderLeft: '4px solid var(--color-primary)',
                          marginBottom: 'var(--spacing-xl)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={aboutInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.7 }}
                        whileHover={{
                          boxShadow: 'var(--shadow-md)',
                          transform: 'translateX(5px)'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Recycle size={32} style={{
                            color: 'var(--color-primary)',
                            marginRight: 'var(--spacing-md)',
                            flexShrink: 0
                          }} />
                          <div>
                            <h4 style={{
                              fontSize: 'var(--font-size-xl)',
                              fontWeight: 'var(--font-weight-semibold)',
                              marginBottom: 'var(--spacing-sm)',
                              color: 'var(--color-text)'
                            }}>
                              Programme educ4us
                            </h4>
                            <p style={{
                              fontSize: 'var(--font-size-base)',
                              lineHeight: 'var(--line-height-relaxed)',
                              color: 'var(--color-text)',
                              margin: 0
                            }}>
                              Vous pouvez contribuer à notre engagement en faveur de l'éducation pour tous à travers
                              notre programme <strong style={{ color: 'var(--color-primary)' }}>educ4us</strong> qui
                              construit des centres multimédia dans des pays en développement, contribuant ainsi au
                              quatrième pilier du développement durable qui est l'accès à l'éducation pour tous.
                            </p>
                          </div>
                        </div>
                      </motion.div>

                      <motion.div
                        style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={aboutInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.8 }}
                      >
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button
                            variant="primary"
                            onClick={() => window.location.href = '/mission'}
                          >
                            En savoir plus sur notre engagement
                            <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
                          </Button>
                        </motion.div>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button
                            variant="outline"
                            onClick={() => window.location.href = '/contact'}
                          >
                            Nous contacter
                          </Button>
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Bannière CTA (gradient bleu → vert) */}
      <section
        style={{
          background: "linear-gradient(90deg, #1e40af 0%, #0f766e 100%)",
          padding: "var(--spacing-4xl) var(--spacing-lg)",
          textAlign: "center"
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <h2
              style={{
                fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)",
                fontWeight: "var(--font-weight-bold)",
                color: "#fff",
                marginBottom: "var(--spacing-lg)"
              }}
            >
              Prêt à optimiser votre gestion des déchets ?
            </h2>
            <p
              style={{
                fontSize: "var(--font-size-lg)",
                color: "rgba(255,255,255,0.95)",
                maxWidth: 560,
                margin: "0 auto var(--spacing-2xl)",
                lineHeight: "var(--line-height-relaxed)"
              }}
            >
              Nous sommes à votre écoute pour concevoir une solution innovante sur-mesure adaptée à votre besoin spécifique.
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }} transition={springTransition}>
              <Button variant="light" size="lg" onClick={() => window.location.href = "/contact"}>
                Demander un devis gratuit
                <ArrowRight size={20} style={{ marginLeft: "0.5rem" }} />
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default Home;
